/* eslint-disable max-lines */
import React, { useRef, useState, useEffect } from 'react';
// @ts-ignore
import { Grid } from './Grid';
import styled from 'styled-components/macro';
import Search from 'components/Input/SearchInput';
import { Sorter } from 'components/Table/styles';
import helper from 'utils';
import { Q1Value } from 'models/documents';
import { BoundingBox } from 'models/response';
import Datepicker from 'components/Input/datepicker/Datepicker';
import { UserDate, UserDecimalSep } from 'models/user';
import { defaultUserDateFormat, defaultUserDecimalSeparator, DEFAULT_WIDTH_Q3_COLUMNS } from 'utils/constants';
import NumberInput from 'components/Input/NumberInput';

function hideRowAtIndex(i: number) {
  return `
      &:nth-child(${i}) {
        display: none
       }
    `;
}

function makeHideRowsCSSRules(rowsIndex: number[]) {
  let str = '';
  rowsIndex.forEach((rowIndex) => {
    str += hideRowAtIndex(rowIndex);
  });
  return str;
}

const TableContainer = styled.div<{ rowVisibility: number[]; heigth: number }>`
  width: 100%;
  overflow-x: auto;
  .SpreadsheetGrid__row {
    ${({ rowVisibility }) => makeHideRowsCSSRules(rowVisibility)}
  }
  .SpreadsheetGridScrollDummy {
    height: ${({ heigth }) => heigth}px !important;
  }
`;

const HeadTitle = styled.div<{ withMargin?: boolean; hasSorting: boolean }>`
  margin-top: ${({ withMargin }) => (withMargin ? '5px' : 'unset')};
  cursor: ${({ hasSorting }) => (hasSorting ? 'pointer' : 'default')};
`;

const HeadTitleFilterOn = styled.div`
  width: 6px;
  height: 6px;
  background: ${({ theme }) => theme.colorPalette.red};
  border-radius: 50%;
  display: inline-block;
  margin-left: 5px;
`;

const SearchNumberFilter = styled.input`
  width: 100%;
  max-width: 100%;
  font-family: ${({ theme }) => theme.fontFamily};
  font-weight: ${({ theme }) => theme.fontWeightPalette.regular};
  border: none;
  border-radius: 9px;
  &:focus {
    outline: 0;
  }
  ::-ms-reveal,
  ::-ms-clear {
    display: none;
  }
`;

export interface PropDataGrid {
  id: string | number;
  headerHeight?: number;
  rowHeight?: number;
  columns: Columns[];
  rows: DataGridRow[];
  hasFilter?: boolean;
  isColumnsResizable?: boolean;
  onColumnResize?: (widths: { [key: string]: number }) => void;
  disabled?: boolean;
  onSelection?: (row: DataGridRow | null, columnId?: number) => void;
  onCellMove?: ({ x, y }: { x: number; y: number }) => void;
  hasSorting?: boolean;
  onFiltersSet?: (hasFilterSet: boolean) => void;
  field?: Q1Value;
  setFiledVal?: (val: Q1Value) => void;
  setGridRef?: (val: any) => void;
}

export interface DataGridRow {
  [key: string]: any;
  id: string;
}

export interface DataGridComplete {
  id: string;
  [colId: number]: {
    content: string;
    pageNumber: number;
    boundingBox: BoundingBox;
  };
}

export interface RowStatus {
  focus: boolean;
  active: boolean;
  disabled: boolean;
}

export interface Columns {
  title: string;
  value: (row: DataGridRow, { focus, active, disabled }: RowStatus) => JSX.Element | null;
  id: string | number;
  width?: number | null;
  type?: string | null;
  dateFormat?: UserDate;
  userSeparator?: UserDecimalSep;
  isResizable?: boolean;
}

const DataGrid = (props: PropDataGrid) => {
  const {
    headerHeight = 27,
    rowHeight = 30,
    columns,
    rows,
    hasSorting = false,
    isColumnsResizable,
    disabled,
    hasFilter,
    onSelection,
    id = '',
    onCellMove,
    onFiltersSet = () => null,
    setGridRef,
  } = props;
  const gridRef = useRef(null);
  const [hiddenRowIndex, setHiddenRowIndex] = useState<number[]>([]);
  const [filters, setFilters] = useState<{ [key: string]: { value: string; exact?: boolean } }>({});
  const [sorting, setSorting] = useState({ colId: '', sort: '' });
  const [internalRows, setInternalRows] = useState(rows);
  const [columnWidths, setColumnWidths] = useState<{ [key: number]: number }>({});
  const activeCell = useRef<{ rowId: string; columnId: number }>({ rowId: '', columnId: -1 });

  useEffect(() => {
    setGridRef && setGridRef(gridRef);
  }, [gridRef, setGridRef]);

  useEffect(() => {
    setInternalRows(rows);
  }, [rows]);

  useEffect(() => {
    setSorting({ colId: '', sort: '' });
  }, [rows.length]);

  const onCellClick = (row: DataGridRow, columnId: number) => {
    // dont trigger the onSelection clicking on the same cell
    if (onSelection && (activeCell.current.rowId !== row.id || activeCell.current.columnId !== columnId)) {
      onSelection(row, columnId);
    }
    activeCell.current = { rowId: row.id, columnId };
  };

  const sortRows = (col: any) => {
    if (hasSorting) {
      const colName = col.id;
      const copyInternalRows = [...internalRows];
      switch (col.type) {
        case 'text':
          if (sorting.sort === 'up') {
            setSorting({ colId: colName, sort: 'down' });
            copyInternalRows.sort((a, b) => ('' + b[colName]).localeCompare(a[colName]));
          } else {
            setSorting({ colId: colName, sort: 'up' });
            copyInternalRows.sort((a, b) => ('' + a[colName]).localeCompare(b[colName]));
          }
          break;
        case 'number':
          if (sorting.sort === 'up') {
            setSorting({ colId: colName, sort: 'down' });
            copyInternalRows.sort((a, b) => parseFloat(b[colName]) - parseFloat(a[colName]));
          } else {
            setSorting({ colId: colName, sort: 'up' });
            copyInternalRows.sort((a, b) => parseFloat(a[colName]) - parseFloat(b[colName]));
          }
          break;
        case 'date':
          if (sorting.sort === 'up') {
            setSorting({ colId: colName, sort: 'down' });
            copyInternalRows.sort((a, b) => {
              const dateA = a[colName] ? helper.date.convertToObjectDate(a[colName]) : '';
              const dateB = b[colName] ? helper.date.convertToObjectDate(b[colName]) : '';
              return dateB > dateA ? 1 : -1;
            });
          } else {
            setSorting({ colId: colName, sort: 'up' });
            copyInternalRows.sort((a, b) => {
              const dateA = a[colName] ? helper.date.convertToObjectDate(a[colName]) : '';
              const dateB = b[colName] ? helper.date.convertToObjectDate(b[colName]) : '';
              return dateA > dateB ? 1 : -1;
            });
          }
          break;
        default:
          return null;
      }
      setInternalRows(copyInternalRows);
    }
  };

  useEffect(() => {
    // an array of index from rows to be excluded from the view by css
    const filteredHiddenRowIndex: number[] = [];
    internalRows.forEach((item, index) => {
      try {
        for (const [key, property] of Object.entries(filters)) {
          if (!item.hasOwnProperty(key)) {
            filteredHiddenRowIndex.push(index + 1);
            break;
          }
          if (property.exact) {
            if (item[key] !== property.value) {
              filteredHiddenRowIndex.push(index + 1);
              break;
            }
          } else {
            if (!item[key].includes(property.value)) {
              filteredHiddenRowIndex.push(index + 1);
              break;
            }
          }
        }
      } catch (e) {
        console.error(e);
      }
    });
    setHiddenRowIndex(filteredHiddenRowIndex);
  }, [filters, internalRows]);

  useEffect(() => {
    setHiddenRowIndex([]);
    setFilters({});
    onFiltersSet(false);
  }, [internalRows.length, onFiltersSet]);

  // resetting filters to default
  useEffect(() => {
    setFilters({});
    onFiltersSet(false);
  }, [id, onFiltersSet]);

  const updateFilters = (value: string, name: string, exact?: boolean) => {
    let newFilters;
    if (value.length > 0) {
      newFilters = { ...filters, [name]: { value, exact } };
    } else {
      newFilters = { ...filters };
      // clean filter object in case of empty value
      delete newFilters[name];
    }
    setFilters(newFilters);
    let hasFiltersSet = false;
    for (const value of Object.values(newFilters)) {
      hasFiltersSet = hasFiltersSet || Boolean(value);
    }
    onFiltersSet(hasFiltersSet);
  };

  // saving all filters values on change
  const onFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = e.target;
    updateFilters(value, name);
  };

  // number filters
  const onFilterNumberChange = (value: any, name: string) => {
    updateFilters(value ? value.toString() : '', name);
  };

  // saving all filters values on change
  const onFilterDataChange = (date: Date | null, name: string) => {
    const value = date ? helper.date.formatDateForDb(date) : '';
    updateFilters(value, name, true);
  };

  // Change columns width values
  const onColumnResize = (widthValues: { [key: number]: number }) => {
    setColumnWidths(widthValues);
  };

  const getInputFormat = (column: Columns) => {
    switch (column.type) {
      case 'date':
        return (
          <Datepicker
            dateFormat={column.dateFormat || defaultUserDateFormat}
            inputWidth={'100%'}
            isClearable
            fontSize={10}
            small
            noBorder
            portal
            onChange={(date) => onFilterDataChange(date, `${column.id}`)}
          />
        );
      case 'text':
        return (
          <Search
            name={`${column.id}`}
            placeholder=""
            fullWidth
            small
            value={filters[column.id]?.value || ''}
            onChange={onFilterChange}
          />
        );
      case 'number':
        return (
          <NumberInput
            customInput={SearchNumberFilter}
            onChange={(value) => onFilterNumberChange(value, `${column.id}`)}
            decimalSeparator={column.userSeparator ? column.userSeparator : defaultUserDecimalSeparator}
            hasFixedDecimals
          />
        );
    }
  };

  const enhancedColumns = () => {
    return columns.map((column) => ({
      ...column,
      // For text columns (isResizable: false), use the width passed as prop
      // For other columns, use internal columnWidths or default
      width:
        column.isResizable === false
          ? column.width ?? DEFAULT_WIDTH_Q3_COLUMNS
          : columnWidths[column.id as number] ?? column.width ?? DEFAULT_WIDTH_Q3_COLUMNS,
      isResizable: column.isResizable !== false, // false per colonne di testo
      title: hasFilter
        ? () => (
            <>
              {getInputFormat(column)}
              <HeadTitle hasSorting={hasSorting} onClick={() => sortRows(column)} withMargin>
                {<Sorter isSorted={sorting.colId === column.id} isSortedDesc={sorting.sort === 'down'} />}
                {column.title} {filters[column.id] ? <HeadTitleFilterOn /> : ''}
              </HeadTitle>
            </>
          )
        : () => (
            <HeadTitle hasSorting={hasSorting} onClick={() => sortRows(column)} withMargin>
              {<Sorter isSorted={sorting.colId === column.id} isSortedDesc={sorting.sort === 'down'} />}
              {column.title} {filters[column.id] ? <HeadTitleFilterOn /> : ''}
            </HeadTitle>
          ),
    }));
  };

  const height = (internalRows.length - hiddenRowIndex.length) * rowHeight;
  return (
    <TableContainer rowVisibility={hiddenRowIndex} heigth={height < 0 ? 0 : height}>
      <Grid
        ref={gridRef}
        disabledCellChecker={() => disabled || false}
        active
        onCellMove={onCellMove}
        columns={enhancedColumns()}
        rows={internalRows}
        isColumnsResizable={isColumnsResizable}
        onColumnResize={onColumnResize}
        getRowKey={(row: DataGridRow) => row.id}
        headerHeight={hasFilter ? headerHeight + 23 : headerHeight}
        rowHeight={rowHeight}
        onCellClick={onCellClick}
        onActiveCellChanged={(cell: { x: number; y: number } | null) => {
          if (gridRef.current) {
            // @ts-ignore
            gridRef.current.focusCell(cell);
          }
          if (cell === null) {
            onSelection && onSelection(null);
          } else {
            onSelection && onSelection(internalRows[cell.x]);
          }
        }}
        focusOnSingleClick={true}
      />
    </TableContainer>
  );
};

export default DataGrid;
